/* eslint-disable @typescript-eslint/no-unused-vars */
/* eslint-disable react-hooks/exhaustive-deps */

"use client";

import {
  useEffect, useMemo, useRef, useState,
} from "react";

import { usePathname } from "next/navigation";

import get from "lodash/get";
import {
  type FieldValues, type UseFormSetError, type Path, type UseFormWatch,
  type UseFormClearErrors, type FieldErrors,
} from "react-hook-form";
import { shallowEqual } from "react-redux";
import { type ZodEffects, type ZodObject } from "zod";

import {
  type ExternalFieldsMap, type ToastErrorsMap, type ToastErrorsMapField, type ToastFieldsMap,
} from "@/providers/error-handler";
import { useAppDispatch, useAppSelector } from "@/providers/store/hooks";
import { deleteClientError } from "@/providers/store/slices/app/slice";
import { useToast } from "@/providers/toasts/toasts.provider";


import { USER_UNKNOWN_ERROR } from "../constants";
import { flattenObject } from "../helpers/flatten-object.helper";

import { useClearClientErrors } from "./clear-client-errors.hook";

const areValuesEqual = (a: unknown, b: unknown): boolean => JSON.stringify(a) === JSON.stringify(b);

export const useClientErrors = <T extends FieldValues>(
  setError: UseFormSetError<T>,
  watch: UseFormWatch<T>,
  clearErrors: UseFormClearErrors<T>,
  errors: FieldErrors<T>,
  config?: {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    schema?: ZodObject<any> | ZodEffects<ZodObject<any>>,
    fields?: Path<T>[];
    toastFields?: ToastFieldsMap<T>;
    externalFieldsMap?: ExternalFieldsMap<T>;
    showUnhandledErrorInToast?: boolean;
    unhandledErrorFieldsMap?: Record<
      string,
      Path<T>[]
    >;
  },
) => {
  const {
    fields = [],
    toastFields = {},
    externalFieldsMap = {},
    showUnhandledErrorInToast = false,
    unhandledErrorFieldsMap = {},
    schema,
  } = config || {};

  const dispatch = useAppDispatch();
  const { triggerClearErrors } = useClearClientErrors();
  const pathname = usePathname();
  const clientErrors = useAppSelector(
    (state) => state.app.clientErrors[pathname],
    shallowEqual,
  );

  const [unhandledError, setUnhandledError] = useState<string | null>(null);
  const [toastErrors, setToastErrors] = useState<ToastErrorsMap<T>>({} as ToastErrorsMap<T>);

  const { showToast } = useToast();

  const memoizedFields = useMemo(() => fields, []);
  const memoizedToastFields = useMemo(() => toastFields, []);
  const memoizedExternalFieldsMap = useMemo(() => externalFieldsMap, []);

  const [previousValues, setPreviousValues] = useState<Record<string, unknown>>({});

  const [errorsState, setErrors] = useState(errors);

  useEffect(() => {
    setErrors(errors);
  }, [!areValuesEqual(errorsState, errors)]);

  useEffect(() => {
    const subscription = watch((allFields) => {
      const flattenedCurrent = flattenObject(allFields);
      const flattenedPrevious = flattenObject(previousValues.current);

      Object.keys(flattenedCurrent).forEach((path) => {
        const prevVal = flattenedPrevious[path];
        const currVal = flattenedCurrent[path];

        if (!areValuesEqual(prevVal, currVal) && currVal) {
          if (get(errorsState, path)) {
            clearErrors(path as Path<T>);
            dispatch(deleteClientError(pathname));
          }
          if (unhandledError) {
            setUnhandledError(null);
          }
        }
      });

      setPreviousValues(allFields);
    });

    return () => subscription.unsubscribe();
  }, [watch, clearErrors, unhandledError, errorsState]);

  useEffect(() => {
    setUnhandledError(clientErrors?.message as string || null);

    return () => {
      setUnhandledError(null);
      setToastErrors({} as ToastErrorsMap<T>);
    };
  }, [clientErrors, setError, memoizedFields, memoizedToastFields, memoizedExternalFieldsMap]);

  useEffect(() => {
    if (toastErrors && Object.keys(toastErrors).length > 0) {
      Object.entries(toastErrors).forEach(([_, errMsg]) => {
        if (errMsg) {
          const { message, description } = errMsg as ToastErrorsMapField;
          showToast({
            type: "error",
            message,
            description,
          });
        }
      });
    }
  }, [toastErrors, showToast]);

  useEffect(() => {
    if (unhandledError && showUnhandledErrorInToast) {
      showToast({
        type: "error",
        message: USER_UNKNOWN_ERROR,
        description: unhandledError,
      });
    }
  }, [unhandledError, showUnhandledErrorInToast, showToast]);

  return { unhandledError, toastErrors, triggerClearErrors };
};
